import { Tooltip } from '@mantine/core';
import classnames from 'classnames';
import { type PropsWithChildren, useMemo } from 'react';

import Button from '@/shared/ui/Button';
import WarningBanner from '@/shared/ui/WarningBanner';
import { useWiwSelector } from '@/store';
import { getConversationsLoaded } from '@/workchat/v2/store/conversations/conversationsSelectors';
import { getWorkchatConnected, getWorkchatInitError } from '@/workchat/v2/store/selectors';

import '@/workchat/v3/components/HeaderV3/HeaderV3.scss';

export type HeaderButton = {
  action: () => void;
  className?: string;
  disabled?: boolean;
  label: (string | JSX.Element)[] | string | JSX.Element;
  title?: string;
  side: 'left' | 'right';
  color?: 'secondary' | 'primary';
  circle?: boolean;
  tooltip?: string;
};

interface HeaderProps {
  buttons?: HeaderButton[];
  borderBottom?: boolean;
}

export default function HeaderV3({ buttons, borderBottom, children }: PropsWithChildren<HeaderProps>) {
  const connected = useWiwSelector(getWorkchatConnected);
  const initError = useWiwSelector(getWorkchatInitError);
  const conversationsLoaded = useWiwSelector(getConversationsLoaded);

  const leftButtons = useMemo(() => {
    return buttons?.filter(button => button.side === 'left');
  }, [buttons]);

  const rightButtons = useMemo(() => {
    return buttons?.filter(button => button.side === 'right');
  }, [buttons]);

  const createButton = useMemo(() => {
    return (button: HeaderButton, index: number) => {
      const out = (
        <Button
          type="button"
          key={`head-button-${button.title}-${index}`}
          title={button.title}
          onClick={button.action}
          className={button.className}
          disabled={button.disabled || false}
          size="sm"
          color={button.color}
          circle={button.circle}
        >
          {button.label}
        </Button>
      );

      if (button.tooltip) {
        return <Tooltip label={button.tooltip}>{out}</Tooltip>;
      }

      return out;
    };
  }, [buttons, connected]);

  if (!buttons?.length && !children) {
    return null;
  }

  const renderDisconnectedBanner = () => {
    if (connected || initError || !conversationsLoaded) {
      return null;
    }

    return (
      <div className="col-12">
        <WarningBanner icon="warning" level="error" wrapperClass="disconnected-banner">
          Service unavailable, trying to reconnect...
        </WarningBanner>
      </div>
    );
  };

  return (
    <div className={classnames('HeaderV3 row', { 'border-bottom': borderBottom })}>
      <div className="col-12">
        <div className="row justify-content-between align-items-center header">
          <div className="col-3">
            <div className="row no-gutters float-left align-items-center">
              {leftButtons?.map((button, idx) => createButton(button, idx))}
            </div>
          </div>
          {children ? <h2 className="col-6 header">{children}</h2> : <div className="filler" />}
          <div className="col-3">
            <div className="row no-gutters float-right align-items-center">
              {rightButtons?.map((button, idx) => createButton(button, idx))}
            </div>
          </div>
        </div>
      </div>
      {renderDisconnectedBanner()}
    </div>
  );
}
